import { Callout, Intent } from '@blueprintjs/core'
import { lang } from 'botpress/shared'
import axios from 'axios'
import React, { FC, useState } from 'react'
import { Link, RouteComponentProps } from 'react-router-dom'

import { ChangePasswordForm } from './ChangePasswordForm'
import LoginContainer from './LoginContainer'
import style from './style.scss'

type Props = RouteComponentProps

/**
 * Step 2 of the self-service reset: consume the emailed token.
 *
 * Reuses ChangePasswordForm so the strength meter and confirmation rules match
 * the forced-change screen. The token is read from the query string and only
 * ever posted back — it is never rendered on screen.
 */
export const ResetPassword: FC<Props> = props => {
  const params = new URLSearchParams(props.location.search)
  const token = params.get('token') || ''
  const strategy = params.get('strategy') || 'default'

  const [error, setError] = useState<string>()
  const [done, setDone] = useState(false)

  const submit = async (newPassword: string, confirmPassword: string) => {
    if (newPassword !== confirmPassword) {
      return setError(lang.tr('admin.passwordsDontMatch'))
    }

    try {
      setError(undefined)
      await axios.post(`${window['API_PATH']}/auth/reset-password/${strategy}`, { token, newPassword })
      setDone(true)
    } catch (err) {
      if (err.response?.status === 404) {
        setError(lang.tr('admin.passwordResetDisabled'))
      } else if (err.response?.status === 429) {
        setError(lang.tr('admin.passwordResetTooManyRequests'))
      } else {
        // Covers expired, already used and malformed tokens alike: the server
        // deliberately does not tell them apart, so the localized copy is always
        // right — and it avoids leaking the error class name the API prefixes.
        setError(lang.tr('admin.passwordResetInvalidToken'))
      }
    }
  }

  if (!token) {
    return (
      <LoginContainer subtitle={lang.tr('admin.setANewPassword')}>
        <Callout intent={Intent.DANGER}>{lang.tr('admin.passwordResetInvalidToken')}</Callout>
        <p className={style.forgotPasswordHint}>
          <Link to="/forgot-password">{lang.tr('admin.requestNewResetLink')}</Link>
        </p>
      </LoginContainer>
    )
  }

  if (done) {
    return (
      <LoginContainer subtitle={lang.tr('admin.passwordUpdated')}>
        <Callout intent={Intent.SUCCESS}>{lang.tr('admin.passwordResetSuccess')}</Callout>
        <p className={style.forgotPasswordHint}>
          <Link to="/login">{lang.tr('admin.backToLogin')}</Link>
        </p>
      </LoginContainer>
    )
  }

  return (
    <LoginContainer subtitle={lang.tr('admin.setANewPassword')} error={error}>
      <ChangePasswordForm onChangePassword={submit} email="" />
      <p className={style.forgotPasswordHint}>
        <Link to="/forgot-password">{lang.tr('admin.requestNewResetLink')}</Link>
      </p>
    </LoginContainer>
  )
}

export default ResetPassword
